import { PivotLevelDataPoint } from '../../../api/metrics';
import { PivotChartDataPoint } from '../../../types/pivotPoints.ts';

/**
 * Transform API response pivot data to Recharts-compatible format
 */
export function transformPivotData(apiData: PivotLevelDataPoint[]): PivotChartDataPoint[] {
  return apiData.map(point => ({
    timestamp: new Date(point.timestamp).getTime(),
    pp: point.pivotPoint,
    r1: point.r1,
    r2: point.r2,
    r3: point.r3,
    s1: point.s1,
    s2: point.s2,
    s3: point.s3,
  }));
}

/**
 * Merge price data with pivot data by matching timestamps
 */
export function mergePriceData(
  pivotData: PivotChartDataPoint[],
  priceData: { timestamp: string; close: number }[]
): PivotChartDataPoint[] {
  // Create a map of price data by timestamp
  // Use hour-level precision for better matching with intraday pivots
  const priceMap = new Map<string, number>();

  priceData.forEach(p => {
    try {
      // p.timestamp should be an ISO-8601 string (e.g., "2024-01-15T12:00:00Z")
      if (!p.timestamp) return;

      const date = new Date(p.timestamp);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp in price data:', p.timestamp);
        return;
      }

      // Create keys at different granularities for better matching
      const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const hourKey = date.toISOString().substring(0, 13); // YYYY-MM-DDTHH

      // Store with both keys - hour key for intraday, day key for daily/weekly/monthly
      priceMap.set(hourKey, p.close);
      if (!priceMap.has(dayKey)) {
        priceMap.set(dayKey, p.close); // Use first price of the day as fallback
      }
    } catch (error) {
      console.warn('Error processing price timestamp:', p.timestamp, error);
    }
  });

  // Merge price into pivot data
  return pivotData.map(point => {
    try {
      // point.timestamp is a number (milliseconds) from transformPivotData
      const date = new Date(point.timestamp);

      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp in pivot data:', point.timestamp);
        return point;
      }

      // Try hour-level match first (for intraday pivots), then fall back to day-level
      const hourKey = date.toISOString().substring(0, 13); // YYYY-MM-DDTHH
      const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

      const price = priceMap.get(hourKey) || priceMap.get(dayKey);

      return {
        ...point,
        price,
      };
    } catch (error) {
      console.warn('Error processing pivot timestamp:', point.timestamp, error);
      return point;
    }
  });
}
